import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { aboutAPI } from '../utils/api';
import { Mail, MapPin, Award, Users } from 'lucide-react';
import profileImg from '../assets/profile.png';

const About = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAbout();
  }, []);

  const loadAbout = async () => {
    try {
      const response = await aboutAPI.get();
      setAbout(response.data);
    } catch (error) {
      console.error('Error loading about:', error);
    } finally {
      setLoading(false);
    }
  };

  const defaultBio = "Creative graphic designer with a passion for visual storytelling. I blend artistry with strategy to create impactful designs that resonate with audiences.";
  const defaultSkills = ['Branding', 'Web Design', 'UI/UX', 'Illustration', 'Print Design', 'Typography', 'Adobe Creative Suite', 'Figma'];

  // Use API profile image if available, otherwise fallback to local profile image
  const profileImage = about?.profile_image && about.profile_image.trim() !== '' ? about.profile_image : profileImg;

  const stats = [
    { icon: Award, label: "Years Experience", value: "5+" },
    { icon: Users, label: "Happy Clients", value: "50+" },
    { icon: MapPin, label: "Projects Completed", value: "100+" },
  ];

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24" data-testid="about-page">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tighter leading-[0.9] mb-16" data-testid="about-title">
            About Me
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-square overflow-hidden border border-white/10 rounded-lg hover:border-primary/50 transition-all" data-testid="profile-image">
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <motion.div 
              className="flex flex-col justify-center space-y-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold font-['Syne'] tracking-tight mb-2">
                  Hi, I'm Debojit
                </h2>
                <p className="text-xl text-primary font-medium mb-6">
                  Developer, Creative Designer & Visual Storyteller
                </p>
                <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-6" data-testid="bio-text">
                  {about?.bio || defaultBio}
                </p>
                <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                  I specialize in crafting compelling visual narratives that bridge the gap between creativity and functionality. My approach combines innovative design thinking with user-centered principles to deliver exceptional results.
                </p>
              </div>
              
              <div className="flex items-center gap-3 text-muted-foreground">
                <Mail className="w-5 h-5 text-primary" />
                <span>Available for freelance projects</span>
              </div>
            </motion.div>
          </div>

          {/* Stats Section */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="bg-secondary/50 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all group"
                whileHover={{ y: -5 }}
              >
                <stat.icon className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                <p className="text-3xl font-bold font-['Syne'] mb-1">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Companies Section */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3 className="text-2xl md:text-3xl font-semibold font-['Syne'] tracking-tight mb-8">
              Trusted by Leading Companies
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {['WhyMedia', 'CodeFlare Labs', 'Mentorsity', 'IFO', 'ElevateLabs'].map((company, index) => (
                <motion.div
                  key={index}
                  className="bg-secondary/30 border border-white/10 rounded-lg p-6 flex items-center justify-center hover:border-primary/50 hover:bg-secondary/50 transition-all group min-h-[100px]"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.6 + (index * 0.1) }}
                  whileHover={{ y: -5, scale: 1.05 }}
                >
                  <span className="text-sm md:text-base font-medium text-center group-hover:text-primary transition-colors">
                    {company}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Skills Section */}
          <motion.div 
            data-testid="skills-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <h3 className="text-2xl md:text-3xl font-semibold font-['Syne'] tracking-tight mb-8">
              Skills & Expertise
            </h3>
            <div className="flex flex-wrap gap-3">
              {(about?.skills && about.skills.length > 0 ? about.skills : defaultSkills).map((skill, index) => (
                <motion.span
                  key={index}
                  className="px-6 py-3 bg-secondary border border-white/10 rounded-full text-sm font-medium hover:border-primary/50 hover:bg-primary/10 transition-all cursor-default"
                  data-testid={`skill-${index}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.6 + (index * 0.05) }}
                  whileHover={{ scale: 1.05 }}
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Experience Timeline Section */}
          <motion.div 
            className="mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h3 className="text-2xl md:text-3xl font-semibold font-['Syne'] tracking-tight mb-8">
              Experience & Journey
            </h3>
            <div className="space-y-6">
              <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                  <h4 className="text-xl font-semibold font-['Syne']">Graphic Designer</h4>
                  <span className="text-sm text-primary font-medium">2023 - Present</span>
                </div>
                <p className="text-muted-foreground mb-2">Leading design projects across multiple companies</p>
                <p className="text-sm text-muted-foreground">
                  Collaborated with WhyMedia, CodeFlare Labs, Mentorsity, IFO, and ElevateLabs to deliver exceptional design solutions.
                </p>
              </div>

              <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                  <h4 className="text-xl font-semibold font-['Syne']">Freelance Graphic Designer</h4>
                  <span className="text-sm text-primary font-medium">2021 - Present</span>
                </div>
                <p className="text-muted-foreground mb-2">Designing brand identities and visual systems for growing businesses</p>
                <p className="text-sm text-muted-foreground">
                  I build cohesive brand experiences through logos, YouTube thumbnails, typography, and marketing assets..
                </p>
              </div>

              <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                  <h4 className="text-xl font-semibold font-['Syne']">Junior Designer</h4>
                  <span className="text-sm text-primary font-medium">2020 - 2021</span>
                </div>
                <p className="text-muted-foreground mb-2">Started my professional journey</p>
                <p className="text-sm text-muted-foreground">
                  Learned and developed skills in UI/UX design, branding, and digital media production.
                </p>
              </div>
            </div>
          </motion.div>

          {/* What I Do Section */}
          <motion.div 
            className="mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <h3 className="text-2xl md:text-3xl font-semibold font-['Syne'] tracking-tight mb-8">
              What I Do
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <h4 className="text-xl font-semibold mb-3 font-['Syne']">Brand Identity</h4>
                <p className="text-muted-foreground leading-relaxed">
                  Creating unique visual identities that capture your brand's essence and resonate with your target audience.
                </p>
              </div>
              <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <h4 className="text-xl font-semibold mb-3 font-['Syne']">UI/UX Design</h4>
                <p className="text-muted-foreground leading-relaxed">
                  Designing intuitive and beautiful user interfaces that provide seamless digital experiences.
                </p>
              </div>
            <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <h4 className="text-xl font-semibold mb-3 font-['Syne']">Developer</h4>
                <p className="text-muted-foreground leading-relaxed">
                  Building apps and websites, creating automation tools, and developing efficient Python scripts.
                </p>
            </div>
              <div className="bg-secondary/30 border border-white/10 rounded-lg p-6 hover:border-primary/50 transition-all">
                <h4 className="text-xl font-semibold mb-3 font-['Syne']">Graphic Design</h4>
                <p className="text-muted-foreground leading-relaxed">
                  Crafting compelling visual content for digital and print media that tells your story.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
